// Google Analytics 4 integration
// Production only - development does not track

const GA_MEASUREMENT_ID = 'G-J9TC62PFCV';
const isProduction = process.env.VUE_APP_ENDPOINTS === 'production';

// Initialize GA4 for production only
if (isProduction && typeof window !== 'undefined') {
  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
}

export const EVENT_CATEGORIES = {
  APP_EVENT: 'App Event',
  LOGIN_PAGE: 'Login Page Event',
  ARTWORK_PAGE: 'Artwork Page Event',
  ANIMATION_PAGE: 'Animation Page Event',
  PROCESSING_PAGE: 'Processing Page Event',
  PREVIEW_PAGE: 'Preview Page Event',
  SUBSCRIPTION_EVENT: 'Subscription Event'
};

export const APP_ACTIONS = {
  LAUNCH: 'App launched',
  QUIT: 'App quit'
};

export const LANDING_PAGE_ACTIONS = {
  BEGIN_CLICKED: 'Begin button clicked'
};

export const LOGIN_PAGE_ACTIONS = {
  LOGIN_CLICKED: 'Login button clicked',
  SIGNUP_CLICKED: 'Signup button clicked'
};

export const ARTWORK_PAGE_ACTIONS = {
  ARTWORK_SELECTED: 'Artwork selected',
  PROGRESS_INDICATOR_CLICKED: 'Progress indicator clicked',
  HOT_TIP_CLICKED: 'Hot tip link clicked',
  NEXT_CLICKED: 'Next button clicked'
};

export const ANIMATION_PAGE_ACTIONS = {
  ANIMATION_SELECTED: 'Animation selected',
  SOUND_SELECTED: 'External audio selected',
  PROGRESS_INDICATOR_CLICKED: 'Progress indicator clicked',
  HOT_TIP_CLICKED: 'Hot tip link clicked',
  CREATE_CLICKED: 'Create button clicked'
};

export const PROCESSING_PAGE_ACTIONS = {
  TRANSCODE_CANCELLED: 'Transcode cancelled',
  TRANSCODE_COMPLETE: 'Transcode complete'
};

export const PREVIEW_PAGE_ACTIONS = {
  PROGRESS_INDICATOR_CLICKED: 'Progress indicator clicked',
  DONE_CLICKED: 'Done button clicked',
  CREATE_ANOTHER_CLICKED: 'Create Another button clicked'
};

export const SUBSCRIPTION_ACTIONS = {
  PANELS_PUBLISH_UPGRADE_PROMPT_CLICKED: 'Panels publish upgrade prompt clicked',
  PANELS_SIZE_UPGRADE_PROMPT_CLICKED: 'Panels file size upgrade prompt clicked',
  DURATION_UPGRADE_PROMPT_CLICKED: 'Animation duration upgrade prompt clicked',
  SUBSCRIPTIONS_PAGE_OPENED: 'Subscriptions window opened',
  STRIPE_WINDOW_OPENED: 'Stripe page opened'
};

/**
 * Track a custom event
 * @param {string} category - Event category
 * @param {string} action - Event action
 * @param {string} label - Event label (optional)
 * @param {number} value - Event value (optional)
 */
export function trackEvent(category, action, label, value) {
  if (isProduction && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });
  } else {
    console.debug('[Analytics]', { category, action, label, value });
  }
}

/**
 * Track a screen/page view
 * @param {string} screenName - Name of the screen/page
 */
export function trackScreenView(screenName) {
  if (isProduction && window.gtag) {
    window.gtag('event', 'screen_view', {
      screen_name: screenName
    });
  } else {
    console.debug('[Analytics screen]', screenName);
  }
}

/**
 * Track event with custom parameters
 * @param {Object} params - Event parameters
 */
export async function trackEventParams(params) {
  if (isProduction && window.gtag) {
    const { action, ...rest } = params;
    window.gtag('event', action || 'custom_event', rest);
  } else {
    console.debug('[Analytics params]', params);
  }
  return Promise.resolve();
}

/**
 * Track an error/exception
 * @param {string} description - Error description
 * @param {boolean} fatal - Whether the error is fatal
 */
export function trackError(description, fatal) {
  if (isProduction && window.gtag) {
    window.gtag('event', 'exception', {
      description: description,
      fatal: fatal
    });
  } else {
    console.warn('[Analytics error]', { description, fatal });
  }
}
